/* eslint max-lines: 0 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getSharedAudioContributionsLink,
  getSharedAudioDownloadLink,
  getSharedAudioLyricsLink,
  getSharePlaylistGuid,
} from 'app/store/Shares/selectors';
import {
  downloadSharedAudioEffect,
  fetchSharedResourceEffect,
} from 'app/store/Shares/effects';
import { useContextMenu } from 'app/components/ChakraTableList';
import {
  getColumns,
  getMixPanelprops,
} from 'app/components/Playlist/ReadOnlyPlaylist/utils';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import {
  openSharedContactCardAction,
  openSharedCreditsAction,
  openSharedLyricsAction,
} from 'app/store/Mixpanel/slice';
import { ShareType } from 'app/components/Shares/types';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { Text } from '@chakra-ui/react';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { PlaylistAudioReadOnlyLyricsDialog } from '../PlaylistAudioReadOnlyLyricsDialog';
import {
  Contribution,
  ContributionsResponse,
  LyricsResponse,
  ReadOnlyPlaylistProps,
} from './types';
import { PlaylistAudioCreditsDialog } from '../PlaylistAudioCreditsDialog';
import { PlaylistAudioNoteDialog } from '../PlaylistAudioNoteDialog';
import { ReadOnlyPlaylistAudioRow } from './ReadOnlyPlaylistAudioRow';

export const ReadOnlyPlaylist = ({
  shareType,
  onDownload,
  playlist,
  playlistDetails,
  openDescriptionDialog,
}: ReadOnlyPlaylistProps) => {
  const queue = playlist?.queue || [];
  const shareToken = useAppSelector(getSharePlaylistGuid);
  const loading = !playlist;
  const [
    viewedNoteAudio,
    setViewedNoteAudio,
  ] = useState<AudioPlayerAudioInstance | null>(null);
  const [lyricsModal, setLyricsModal] = useState<{
    message?: string;
    subTitle?: string;
  } | null>(null);

  const [creditsModal, setCreditsModal] = useState<{
    subTitle?: string;
    credits: Contribution[];
    onContactCardOpen: () => void;
  } | null>(null);

  const dispatch = useAppDispatch();
  const sharedAudioDownloadLink = useSelector(getSharedAudioDownloadLink);
  const showLyrics = useSelector(getSharedAudioLyricsLink);
  const showCredits = useSelector(getSharedAudioContributionsLink);
  const showDownload = Boolean(onDownload || sharedAudioDownloadLink);

  const actionsCount = useMemo(() => {
    return [showDownload, showLyrics, showCredits].filter(Boolean).length;
  }, [showDownload, showLyrics, showCredits]);

  const handleDownload = useCallback(
    (playlistAudio: AudioPlayerAudioInstance) => {
      if (onDownload) {
        onDownload();
        return;
      }
      if (sharedAudioDownloadLink) {
        dispatch(
          downloadSharedAudioEffect({
            link: sharedAudioDownloadLink,
            guid: playlistAudio.audioGuid || '',
          })
        );
      }
    },
    [dispatch, onDownload, sharedAudioDownloadLink]
  );

  const handleClickLyrics = useCallback(
    async (playlistAudio: AudioPlayerAudioInstance) => {
      dispatch(
        openSharedLyricsAction(getMixPanelprops(playlistAudio, shareType))
      );
      const { lyricSheet }: LyricsResponse = await dispatch(
        fetchSharedResourceEffect({
          shareToken,
          audioGuid: playlistAudio.audioGuid,
        })
      ).unwrap();
      setLyricsModal({
        subTitle: playlistAudio?.audioTitle,
        message: lyricSheet?.body,
      });
    },
    [dispatch, shareToken, shareType]
  );

  const handleClickCredits = useCallback(
    async (playlistAudio: AudioPlayerAudioInstance) => {
      dispatch(
        openSharedCreditsAction(getMixPanelprops(playlistAudio, shareType))
      );
      const { contributions }: ContributionsResponse = await dispatch(
        fetchSharedResourceEffect({
          shareToken,
          audioGuid: playlistAudio.audioGuid,
          resourceType: 'contributions',
        })
      ).unwrap();
      setCreditsModal({
        subTitle: playlistAudio?.audioTitle,
        credits: contributions,
        onContactCardOpen: () => {
          dispatch(
            openSharedContactCardAction(
              getMixPanelprops(playlistAudio, shareType)
            )
          );
        },
      });
    },
    [dispatch, shareToken, shareType]
  );

  const dataTestId =
    shareType === ShareType.AUDIO ? 'shared-audios' : 'playlist-audio-list';

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = `shared-${shareType}`;
  const contextMenuProps = useContextMenu();
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(
    () => getColumns(actionsCount, totalWidth, shareType),
    [actionsCount, totalWidth, shareType]
  );

  const rowRenderer = useCallback(
    (item: Row<AudioPlayerAudioInstance>, index) => {
      const playlistAudio = item.original;
      const options = [
        ...(onDownload || sharedAudioDownloadLink
          ? [
              {
                value: 'download',
                title: 'Download',
                onClick: () => handleDownload(playlistAudio),
              },
            ]
          : []),
        ...(showLyrics
          ? [
              {
                value: 'showLyrics',
                title: 'Show Lyrics',
                onClick: () => handleClickLyrics(playlistAudio),
              },
            ]
          : []),
        ...(showCredits
          ? [
              {
                value: 'showCredits',
                title: 'Show Credits',
                onClick: () => handleClickCredits(playlistAudio),
              },
            ]
          : []),
      ];

      return (
        <ReadOnlyPlaylistAudioRow
          key={playlistAudio.playlistAudioGuid}
          index={index}
          audio={playlistAudio}
          setViewedNoteAudio={setViewedNoteAudio}
          showDownload={showDownload}
          showLyrics={!!showLyrics}
          showCredits={!!showCredits}
          handleDownload={handleDownload}
          handleClickLyrics={handleClickLyrics}
          handleClickCredits={handleClickCredits}
          actionsCount={actionsCount}
          shareType={shareType}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [
      onDownload,
      sharedAudioDownloadLink,
      showLyrics,
      showCredits,
      showDownload,
      handleDownload,
      handleClickLyrics,
      handleClickCredits,
      actionsCount,
      contextMenuProps,
      shareType,
    ]
  );

  return (
    <>
      {playlistDetails?.description && (
        <Text
          role="button"
          onClick={openDescriptionDialog}
          data-testid="playlist-description-text"
          maxW="600px"
          noOfLines={2}
          mb={6}
          wordBreak="break-all"
          _hover={{
            textDecoration: 'underline',
          }}
        >
          {playlistDetails?.description}
        </Text>
      )}

      <StickyTable<AudioPlayerAudioInstance>
        data-testid={dataTestId}
        tableKey={tableKey}
        ref={ref}
        data={queue}
        columns={columns}
        rowRenderer={rowRenderer}
        loading={loading}
        contextMenuProps={contextMenuProps}
      />
      <PlaylistAudioNoteDialog
        open={Boolean(viewedNoteAudio)}
        onClose={() => setViewedNoteAudio(null)}
        message={viewedNoteAudio?.note}
        title={viewedNoteAudio?.audioTitle}
      />
      <PlaylistAudioReadOnlyLyricsDialog
        open={Boolean(lyricsModal)}
        onClose={() => setLyricsModal(null)}
        {...lyricsModal}
      />
      <PlaylistAudioCreditsDialog
        open={Boolean(creditsModal)}
        onClose={() => setCreditsModal(null)}
        {...creditsModal}
      />
    </>
  );
};
